import { handleActions } from 'redux-actions';
import { DeviceId, CameraMessage } from 'types';
import {
    TimelineRequestAction,
    TimelineDataReceivedAction,
    TimelineFailureAction,
} from 'actions/timelineActions';
import { LogoutAction } from 'actions/shellActions';
import { SetDateAction } from 'actions/monitoringActions';

//
// Some stats on camera messages from Gregg.
//
// Minimum: 1440 (1 per minute).
// Maximum: 2,592,000 (30 per second).
// Most _signals files at Perth are 67.5 KB (1440 / day).  Largest is 5,462 KB (116,522 records).
//

export type TimelineState = Readonly<{
    loading: boolean;
    messages: CameraMessage[] | null; // Raw data from the camera signals file
    utcDate: Date | null;
    error: Error | null;
}>;

export type State = Readonly<{
    [cameraId: string]: TimelineState;
}>;

export const initialState = Object.freeze<State>({});

export const initialTimelineState = Object.freeze<TimelineState>({
    loading: false,
    messages: null,
    utcDate: null,
    error: null,
});

export type ReducerAction =
    TimelineRequestAction
    | TimelineDataReceivedAction
    | TimelineFailureAction
    // Shell Actions
    | LogoutAction
    // Monitoring Actions
    | SetDateAction
;
export type Reducer = (state: State, action: ReducerAction) => State;
export type DeviceTimelineReducer = (state: TimelineState, action: ReducerAction) => TimelineState;


//
// Handle the second level of state that is specific to a given device.
//
const deviceTimelineReducer: DeviceTimelineReducer = handleActions({
    TIMELINE_REQUEST(state: TimelineState, action: TimelineRequestAction) {
        const { utcDate } = action.payload;
        return {
            loading: true,
            messages: null,
            utcDate,
            error: null,
        };
    },

    TIMELINE_DATA_RECEIVED(state: TimelineState, action: TimelineDataReceivedAction) {
        const { append } = action.meta;
        const messages = append ? [ ...(state.messages || []), ...action.payload ] : action.payload;
        
        // logic to cut old data if we're appending to the messages, limit to 25 hours (1 day + 1 hour of buffer because of function calculate24ishHoursAgo)
        if (append) {
            const yesterdayTimestamp = Date.now() - (25 * 60 * 60 * 1000);  //getting the timestamp from yesterday (25 hours, 60 minutes, 60 seconds, 1000 milliseconds)
            while (messages[0].t < yesterdayTimestamp) {
                messages.shift(); // drop oldest messages
            }
        }

        return {
            ...state,
            loading: false, // TODO: not true if we load in chunks
            messages,
            error: null,
        };
    },

    TIMELINE_FAILURE(state: TimelineState, action: TimelineFailureAction) {
        const error = action.payload;
        return {
            loading: false,
            messages: null,
            utcDate: null,
            error,
        };
    },

    // Handled in the monitoring reducer
    //TIMELINE_SUCCESS(state: TimelineState, action: TimelineSuccessAction) {},

// tslint:disable-next-line:no-any
} as any, initialTimelineState) as any;


//
// Helper method to update a single device slice from the full state object
//
const updateSlice = <A extends ReducerAction>(state: State, deviceId: DeviceId, action: A): State => ({
    ...state,
    [deviceId]: deviceTimelineReducer(state[deviceId] || initialTimelineState, action),
});

//
// Handle the first layer of getting the right slice of state for the device
// specified in the action
//
export const timelineReducer: Reducer = handleActions({
    TIMELINE_REQUEST(state: State, action: TimelineRequestAction) {
        const { deviceId } = action.payload;
        return updateSlice(state, deviceId, action);
    },

    TIMELINE_DATA_RECEIVED(state: State, action: TimelineDataReceivedAction) {
        const { deviceId } = action.meta;
        return updateSlice(state, deviceId, action);
    },

    TIMELINE_FAILURE(state: State, action: TimelineFailureAction) {
        const { deviceId } = action.meta;
        return updateSlice(state, deviceId, action);
    },

    // Handled in the monitoring reducer
    //TIMELINE_SUCCESS(state: State, action: TimelineSuccessAction) {},
    
    
    //
    // Shell Actions
    //
    LOGOUT(state: State, action: LogoutAction) {
        // Blow away all the timeline data when the user logs out
        return initialState;
    },

    //
    // Monitoring Actions
    //
    SET_DATE(state: State, action: SetDateAction) {
        // Blow away all the timeline data when the data changes
        // TODO: don't want to do this when the date changes naturally to a new day... only when user manually navigates away
        return initialState;
    },

// tslint:disable-next-line:no-any
} as any, initialState) as any;



// WEBPACK FOOTER //
// ./src/reducers/timelineReducer.ts